<script setup lang="ts">
import { useHead } from '@vueuse/head';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import CustomizerSummary from '@/components/customizer/CustomizerSummary.vue';

const route = useRoute();

const heroImage = nutshell['static_img/custom-builder/byob_hero_image.jpg'];
const giftDropLogoSvg = nutshell['static_img/digital-gift/gift-drop-logo.svg'];
const requestUrl = computed(() => route.path);

useHead({
  meta: [
    {
      content: `Design your own custom box of delicious snacks! Choose from a selection
        of more than 50 nuts, dried fruits, and sweets. We'll assemble them for you in a
        beautiful 6-divider box. Custom boxes make perfect gifts.`,
      key: 'og:description',
      property: 'og:description',
    },
    { property: 'og:image', content: heroImage, key: 'og:image' },
    { property: 'og:title', content: 'Build Your Own Box | Nuts.com', key: 'og:title' },
    { property: 'og:url', content: 'https://nuts.com/custom/box', key: 'og:url' },
  ],
});
</script>

<template>
  <div class="py-6" itemscope itemtype="http://schema.org/Product">
    <div class="container flex flex-col">
      <div>
        <meta itemprop="brand" content="Nuts.com" />
        <meta itemprop="name" content="Build Your Own Box" />
        <meta
          itemprop="description"
          content="Build your own gift! Customize with 50+ different options of nuts,
            dried fruit, chocolates and more."
        />
        <meta property="og:title" content="Build Your Own Box | Nuts.com" />
        <meta
          property="og:description"
          content="Design your own custom box of delicious snacks! Choose from a selection of more than 50 nuts, dried fruits, and sweets. We'll assemble them for you in a beautiful 6-divider box. Custom boxes make perfect gifts."
        />
        <meta property="og:image" :content="heroImage" />
        <meta property="og:url" content="https://nuts.com/custom/box" />
        <meta itemprop="image" :content="heroImage" />
        <meta itemprop="sku" content="03719-01" />
        <meta itemprop="mpn" content="03719-01" />
        <meta itemprop="url" :content="requestUrl" />
        <div itemprop="offers" itemscope itemtype="http://schema.org/AggregateOffer">
          <meta itemprop="sku" content="03719-01" />
          <meta itemprop="lowPrice" content="20.00" />
          <meta itemprop="highPrice" content="74.00" />
          <meta itemprop="offerCount" content="1" />
          <meta itemprop="priceCurrency" content="USD" />
          <meta itemprop="priceValidUntil" content="2200-01-01" />
          <meta itemprop="itemCondition" href="http://schema.org/NewCondition" />
          <meta itemprop="availability" content="InStock" />
          <meta itemprop="url" :content="requestUrl" />
        </div>
      </div>
      <div class="relative flex justify-start w-full">
        <div class="item-header">
          <h1 class="font-semibold" data-test="build-your-own-box-title">Build Your Own Box</h1>
        </div>
      </div>
      <div class="flex flex-col ws-md:flex-row">
        <div class="relative flex justify-start w-full ws-md:w-2/3">
          <div id="section" class="h-full">
            <img
              :src="heroImage"
              alt="Custom Box Hero Image"
              class="relative overflow-hidden align-middle image-border rounded-2xl"
              data-test="custom-box-hero-image"
            />
          </div>
        </div>
        <div class="flex w-full ws-md:ml-8 ws-md:w-1/3 ws-md:justify-start">
          <div class="w-full pb-5">
            <CustomizerSummary />
          </div>
        </div>
      </div>

      <RouterLink
        to="/gift-drop"
        class="relative flex flex-col justify-center w-full px-10 py-6 mt-5 mb-6 rounded-lg cursor-pointer bg-nuts-orange-50 md:flex-row hover:no-underline"
      >
        <img :src="giftDropLogoSvg" class="mb-2 md:mb-0" alt="GiftDrop" />
        <div class="text-center md:text-left md:ml-14">
          <div class="flex flex-col mb-2 text-sm font-semibold text-black md:text-xl md:flex-row">
            Let them choose their favorites with GiftDrop!
          </div>
          <div class="text-sm text-black md:text-base">
            No guesswork or mailing address required.
            <span class="underline">Learn&nbsp;More</span>
          </div>
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.image-border {
  border: 2px solid #ddd;
}
</style>
